import type { Events } from '@setplex/pureya'
import type {
  CurrentProgramMediaUrl,
  CurrentTvMediaUrlWithEpg,
} from '~/shared/interfaces'

import { ScheduleType } from '@setplex/tria-api'
import { useMemo } from 'react'
import { validateStorageDuration } from '~/features/rewind/lib'
import { TWO_HOURS_SEC } from '~/shared/constants/time'

export const usePlayerProgress = ({
  enabled,
  mediaUrlData,
}: {
  enabled: boolean
  mediaUrlData: CurrentTvMediaUrlWithEpg | CurrentProgramMediaUrl | null
}): Events.Media.ProgressBarChanged => {
  return useMemo(() => {
    if (!mediaUrlData) return null

    const {
      liveRewindOptions,
      scheduleType,
      startTime: programStartTime,
      endTime: programEndTime,
    } = mediaUrlData

    const hasEpg = scheduleType === ScheduleType.EPG
    const rewindLengthMs =
      (liveRewindOptions?.rewindLengthSec || TWO_HOURS_SEC) * 1000
    const storageDurationSec =
      liveRewindOptions?.storageDurationSec || TWO_HOURS_SEC

    // Current EPG program actual boundaries in milliseconds
    let programStartMs: number, programEndMs: number
    /* Relative seekable boundaries in milliseconds */
    let seekableStartMs: number
    const seekableEndMs: number = 0

    // NOTE Seekable boundaries normalized by "normalizeProgressBar" in some calculations
    if (hasEpg && programStartTime && programEndTime) {
      programStartMs = programStartTime * 1000
      programEndMs = programEndTime * 1000
      seekableStartMs = -rewindLengthMs
    } else {
      programStartMs = -rewindLengthMs
      programEndMs = 0
      seekableStartMs = -rewindLengthMs
    }

    /* Common restrictions */
    seekableStartMs = validateStorageDuration(
      storageDurationSec,
      seekableStartMs
    )

    let liveProgressBar:
      | [number, number, number, number, boolean?]
      | undefined
      | null
      | false
    if (enabled) {
      // if live rewind is allowed - show epg or 2h boundaries
      liveProgressBar = [
        programStartMs,
        seekableStartMs,
        seekableEndMs,
        programEndMs,
      ]
    } else {
      // if live rewind is not allowed
      if (hasEpg) {
        // if epg is available - show epg boundaries
        liveProgressBar = [
          programStartMs,
          seekableStartMs,
          seekableEndMs,
          programEndMs,
          false, // non-interactive progress bar
        ]
      } else {
        // if no epg - show full-width non-interactive progress bar
        liveProgressBar = null
      }
    }

    return liveProgressBar
    // | [number, number, number, number] // [start, seekStart, seekEnd, end]
    // | [number, number, number, number, boolean?] // [start, seekStart, seekEnd, end, isInteractive = true]
    // | null // disabled and non-interactive progress bar (but visible)
    // | false // disabled and hidden progress bar
    // | undefined // no data for live progressBar - vod progressBar showed
  }, [enabled, mediaUrlData])
}
